<template lang="pug">
  transition(name="fade")
    .image-zoom-overlay(v-if="visible" @click.self="close")
      .image-zoom-container
        .image-zoom-toolbar
          .zoom-level {{ zoomPercentage }}%
          .zoom-controls
            button.zoom-btn(@click="zoomOut" :disabled="scale <= minScale" title="Alejar")
              span.material-icons remove
            button.zoom-btn(@click="resetZoom" title="Restablecer")
              span.material-icons fit_screen
            button.zoom-btn(@click="zoomIn" :disabled="scale >= maxScale" title="Acercar")
              span.material-icons add
          button.close-btn(@click="close" title="Cerrar")
            span.material-icons close
        .image-zoom-viewport(
          ref="viewport"
          @mousedown.prevent="startDrag"
          @wheel.prevent="onWheel"
          @touchstart.prevent="onTouchStart"
          @touchmove.prevent="onTouchMove"
          @touchend="onTouchEnd"
          :style="viewportCursor"
        )
          img.zoomable-image(
            :src="src"
            :alt="alt"
            :style="imageTransformStyle"
            draggable="false"
          )
        .image-zoom-footer(v-if="$slots.footer")
          slot(name="footer")
</template>

<script>
export default {
  name: 'ImageZoomViewer',
  props: {
    src: {
      type: String,
      default: ''
    },
    alt: {
      type: String,
      default: 'Imagen'
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      scale: 1,
      minScale: 0.5,
      maxScale: 5,
      zoomStep: 0.25,
      translateX: 0,
      translateY: 0,
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      lastTranslateX: 0,
      lastTranslateY: 0,
      // Touch pinch zoom
      lastPinchDistance: null
    }
  },
  computed: {
    zoomPercentage () {
      return Math.round(this.scale * 100)
    },
    imageTransformStyle () {
      return {
        transform: `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`,
        transformOrigin: 'center center'
      }
    },
    viewportCursor () {
      if (this.isDragging) {
        return { cursor: 'grabbing' }
      }
      return this.scale > 1 ? { cursor: 'grab' } : { cursor: 'default' }
    }
  },
  watch: {
    visible (val) {
      if (val) {
        this.resetZoom()
        document.addEventListener('keydown', this.onKeydown)
      } else {
        document.removeEventListener('keydown', this.onKeydown)
      }
    }
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.onKeydown)
    document.removeEventListener('mousemove', this.onDrag)
    document.removeEventListener('mouseup', this.stopDrag)
  },
  methods: {
    zoomIn () {
      this.scale = Math.min(this.scale + this.zoomStep, this.maxScale)
    },
    zoomOut () {
      this.scale = Math.max(this.scale - this.zoomStep, this.minScale)
      this.constrainTranslate()
    },
    resetZoom () {
      this.scale = 1
      this.translateX = 0
      this.translateY = 0
    },
    onWheel (event) {
      const delta = event.deltaY > 0 ? -this.zoomStep : this.zoomStep
      const newScale = Math.max(this.minScale, Math.min(this.scale + delta, this.maxScale))

      // Zoom hacia el punto del cursor
      if (this.$refs.viewport) {
        const rect = this.$refs.viewport.getBoundingClientRect()
        const mouseX = event.clientX - rect.left - rect.width / 2
        const mouseY = event.clientY - rect.top - rect.height / 2

        const scaleFactor = newScale / this.scale
        this.translateX = mouseX - scaleFactor * (mouseX - this.translateX)
        this.translateY = mouseY - scaleFactor * (mouseY - this.translateY)
      }

      this.scale = newScale
      this.constrainTranslate()
    },
    startDrag (event) {
      this.isDragging = true
      this.dragStartX = event.clientX
      this.dragStartY = event.clientY
      this.lastTranslateX = this.translateX
      this.lastTranslateY = this.translateY
      document.addEventListener('mousemove', this.onDrag)
      document.addEventListener('mouseup', this.stopDrag)
    },
    onDrag (event) {
      if (!this.isDragging) return
      const dx = event.clientX - this.dragStartX
      const dy = event.clientY - this.dragStartY
      this.translateX = this.lastTranslateX + dx
      this.translateY = this.lastTranslateY + dy
    },
    stopDrag () {
      this.isDragging = false
      document.removeEventListener('mousemove', this.onDrag)
      document.removeEventListener('mouseup', this.stopDrag)
    },
    // Touch support for mobile
    onTouchStart (event) {
      if (event.touches.length === 1) {
        this.isDragging = true
        this.dragStartX = event.touches[0].clientX
        this.dragStartY = event.touches[0].clientY
        this.lastTranslateX = this.translateX
        this.lastTranslateY = this.translateY
      } else if (event.touches.length === 2) {
        this.lastPinchDistance = this.getPinchDistance(event.touches)
      }
    },
    onTouchMove (event) {
      if (event.touches.length === 1 && this.isDragging) {
        const dx = event.touches[0].clientX - this.dragStartX
        const dy = event.touches[0].clientY - this.dragStartY
        this.translateX = this.lastTranslateX + dx
        this.translateY = this.lastTranslateY + dy
      } else if (event.touches.length === 2 && this.lastPinchDistance !== null) {
        const currentDistance = this.getPinchDistance(event.touches)
        const scaleDelta = (currentDistance - this.lastPinchDistance) * 0.005
        this.scale = Math.max(this.minScale, Math.min(this.scale + scaleDelta, this.maxScale))
        this.lastPinchDistance = currentDistance
      }
    },
    onTouchEnd () {
      this.isDragging = false
      this.lastPinchDistance = null
    },
    getPinchDistance (touches) {
      const dx = touches[0].clientX - touches[1].clientX
      const dy = touches[0].clientY - touches[1].clientY
      return Math.sqrt(dx * dx + dy * dy)
    },
    constrainTranslate () {
      if (this.scale <= 1) {
        this.translateX = 0
        this.translateY = 0
      }
    },
    onKeydown (event) {
      if (event.key === 'Escape') {
        this.close()
      } else if (event.key === '+' || event.key === '=') {
        this.zoomIn()
      } else if (event.key === '-') {
        this.zoomOut()
      } else if (event.key === '0') {
        this.resetZoom()
      }
    },
    close () {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.image-zoom-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.85);
  z-index: 2000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.image-zoom-container {
  display: flex;
  flex-direction: column;
  width: 90vw;
  height: 90vh;
  max-width: 1200px;
}

.image-zoom-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  background: rgba(30, 30, 30, 0.9);
  border-radius: 8px 8px 0 0;
  backdrop-filter: blur(10px);
  gap: 1rem;
}

.zoom-level {
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  min-width: 50px;
  text-align: center;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.zoom-controls {
  display: flex;
  gap: 0.25rem;
}

.zoom-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #fff;
  border-radius: 6px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.1s ease;
}

.zoom-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.25);
  transform: scale(1.05);
}

.zoom-btn:active:not(:disabled) {
  transform: scale(0.95);
}

.zoom-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.zoom-btn .material-icons {
  font-size: 20px;
}

.close-btn {
  background: rgba(220, 53, 69, 0.6);
  border: 1px solid rgba(220, 53, 69, 0.4);
  color: #fff;
  border-radius: 6px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.1s ease;
}

.close-btn:hover {
  background: rgba(220, 53, 69, 0.9);
  transform: scale(1.05);
}

.close-btn .material-icons {
  font-size: 20px;
}

.image-zoom-viewport {
  flex: 1;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 0 0 8px 8px;
  user-select: none;
  -webkit-user-select: none;
  position: relative;
}

.zoomable-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: transform 0.1s ease-out;
  pointer-events: none;
}

.image-zoom-footer {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(30, 30, 30, 0.9);
  border-radius: 0 0 8px 8px;
  backdrop-filter: blur(10px);
  margin-top: -8px;
}

/* Transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
